import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, CheckCircle2 } from 'lucide-react';
import { requestPasswordReset } from '../lib/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSubmitted(true);
      toast.success('Password reset link sent!');
    } catch (error) {
      // Don't reveal if email exists or not for security
      setSubmitted(true);
      toast.success('If an account exists with this email, you will receive a password reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-teal-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className="w-full max-w-md relative">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
        
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-950 flex items-center justify-center">
                {submitted ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Mail className="w-5 h-5 text-emerald-400" />
                )}
              </div>
              <CardTitle className="text-2xl font-heading">
                {submitted ? 'Check your email' : 'Forgot password?'}
              </CardTitle>
            </div>
            <CardDescription>
              {submitted 
                ? 'We\'ve sent a password reset link to your email address. The link will expire in 1 hour.'
                : 'Enter your email address and we\'ll send you a link to reset your password.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    <strong>Note:</strong> If you don't see the email, please check your spam folder.
                  </p>
                </div>
                <Button 
                  variant="outline"
                  className="w-full rounded-xl h-12"
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                  data-testid="try-again-btn"
                >
                  Try another email
                </Button>
                <Link to="/login">
                  <Button className="w-full rounded-xl h-12" data-testid="back-to-signin-btn">
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="email-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-xl h-12"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </Button>
              </form>
            )}
            
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Remember your password? </span>
              <Link to="/login" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium" data-testid="signin-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
